import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { OddImageOutStyles } from "@ai-teach/canvas-styles";
import type { OddImageOutElement } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { AiFillPicture } from "react-icons/ai";
import { HiPlus } from "react-icons/hi";
import { ChoiceButtons } from "./ChoiceButtons";
import { DeleteImageButton } from "./DeleteImageButton";
import { gradeChoice, gradedRingStyle } from "./gradedStyles";

interface OddImageOutRendererProps {
  element: OddImageOutElement;
  isLiveMode?: boolean;
  /** Index the student picked in the play being replayed. */
  selectedId?: string | undefined;
  /** The question is over: the correct choice turns green whether or not it was picked. */
  isFrozen?: boolean | undefined;
}

export function OddImageOutRenderer({
  element,
  isLiveMode = false,
  selectedId,
  isFrozen,
}: OddImageOutRendererProps) {
  const dispatch = useAppDispatch();
  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { handleFileDrop } = useLocalImageUpload((media, containerId) =>
    handleQuestionDragAndDrop(media, containerId)
  );

  const handleToggleIsCorrectAnswer = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();

    if (index > element.imageChoices.length - 1) return;

    const updatedImageChoices = element.imageChoices.map((choice, i) => ({
      ...choice,
      isCorrectAnswer: i === index,
    }));

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          imageChoices: updatedImageChoices,
        },
      })
    );
  };

  const handleRemoveChoice = (index: number) => {
    if (element.imageChoices.length <= 2) return;

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          imageChoices: element.imageChoices.filter((_, i) => i !== index),
        },
      })
    );
  };

  const handleDeleteImage = (index: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const updatedImageChoices = element.imageChoices.map((choice, i) =>
      i === index ? { ...choice, media: { src: null, filename: null, id: null } } : choice
    );
    dispatch(updateElementProps({ id: element.id, props: { imageChoices: updatedImageChoices } }));
  };

  const handleAddChoice = () => {
    if (element.imageChoices.length > 2) return;

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          imageChoices: [
            ...element.imageChoices,
            { media: { src: null, filename: null, id: null }, isCorrectAnswer: false },
          ],
        },
      })
    );
  };

  return (
    <div
      style={{
        ...OddImageOutStyles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
      }}
    >
      {element.imageChoices.length < 3 && (
        <div onClick={handleAddChoice} className="absolute right-2 top-2 z-10">
          <IconButton
            aria-label="Add choice"
            variant="primary"
            shape="circle"
            size="sm"
            onClick={handleAddChoice}
          >
            <HiPlus className="w-5 h-5" aria-hidden="true" title="Add choice" />
          </IconButton>
        </div>
      )}
      {element?.imageChoices?.map((item, index) => {
        if (!isLiveMode) {
          return (
            <DroppableZone
              key={`${item.media.filename}-${item.isCorrectAnswer}-${index}`}
              id={`${element.id}-oddImageOut-${index}`}
              data={{
                id: `question//oddImageOut//${element.id}//${index}`,
                type: "question-oddImageOut",
              }}
              allowedType={["image"]}
              style={OddImageOutStyles.imageContainer}
              onLocalFileDrop={(file) =>
                handleFileDrop(file, `question//oddImageOut//${element.id}//${index}`)
              }
            >
              {!isLiveMode && (
                <ChoiceButtons
                  isShown={element.imageChoices.length > 2}
                  handleRemoveChoice={handleRemoveChoice}
                  handleToggleIsCorrectAnswer={handleToggleIsCorrectAnswer}
                  isCorrectAnswer={item.isCorrectAnswer}
                  index={index}
                />
              )}
              {item.media.src ? (
                <div className="relative w-full h-full group/image">
                  <img
                    draggable={false}
                    src={item.media.src}
                    alt={item.media.filename || "choice image"}
                    className="w-full h-full object-cover rounded-xl"
                  />
                  <DeleteImageButton onClick={handleDeleteImage(index)} />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-lg flex items-center justify-center">
                  <AiFillPicture className="text-4xl text-slate-400" />
                </div>
              )}
            </DroppableZone>
          );
        }

        return (
          <div
            key={`${item.media.filename}-${item.isCorrectAnswer}-${index}`}
            style={{
              ...OddImageOutStyles.imageContainer,
              ...gradedRingStyle(
                gradeChoice(item.isCorrectAnswer, selectedId === `${index}`, isFrozen)
              ),
            }}
          >
            {!isLiveMode && (
              <ChoiceButtons
                isShown={element.imageChoices.length > 2}
                handleRemoveChoice={handleRemoveChoice}
                handleToggleIsCorrectAnswer={handleToggleIsCorrectAnswer}
                isCorrectAnswer={item.isCorrectAnswer}
                index={index}
              />
            )}
            {item.media.src ? (
              <img
                draggable={false}
                src={item.media.src}
                alt={item.media.filename || "choice image"}
                className="w-full h-full object-cover rounded-xl"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center">
                <AiFillPicture className="text-4xl text-slate-400" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
